import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { IconButton, Link, Tooltip } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import config from '../../config';
import LoadingOverlay from 'react-loading-overlay';
import { makeStyles } from '@material-ui/styles';
import FileUploader from './FileUploader';
import { getMarketLogin } from '../../api/marketLogin';
import SpinningTooltipIconButton from '../SpinBlocking/SpinningTooltipIconButton';
import NotificationDeletion from '../../pages/Home/YourWork/NotificationDeletion';
import { preventDefaultAndProp } from '../../utils/marketIdPathFunctions';
import FileCopyIcon from '@material-ui/icons/FileCopy'

export const attachedFilesStyles = makeStyles(() => ({
  sectionTitle: {
    fontWeight: 700,
    marginBottom: '0.5rem',
  },
  file: {
    wordBreak: 'break-all'
  },
  sidebarContent: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: 0,
    '& span': {
      padding: 0,
      fontSize: '.9375rem',
      fontWeight: 700
    }
  },
  capitalize: {
    textTransform: 'capitalize'
  },
  iconButton: {
    '&:hover': {
      backgroundColor: 'rgba(45,128,237,0.12)',
    },
  },
  iconButtonTight: {
    padding: 6,
  },
}))

function FileCopyLinker(props) {
  const { linkToFile } = props;
  const classes = attachedFilesStyles();
  const intl = useIntl();
  const [copiedCodeToClipboard, setCopiedCodeToClipboard] = useState(false);
  const [inCodeCopy, setInCodeCopy] = useState(false);
  return (
    <IconButton
          className={`${classes.iconButton} ${classes.iconButtonTight}`}
          style={{textTransform: 'none', justifyContent: 'left', whiteSpace: 'nowrap', marginLeft: 0, padding: 2}}
          disableRipple={true}
          onClick={(event) => {
            preventDefaultAndProp(event);
            navigator.clipboard.writeText(linkToFile);
            setCopiedCodeToClipboard(true);
          }}
          onMouseLeave={() => {
            setInCodeCopy(false);
            setCopiedCodeToClipboard(false);
          }}
          onMouseEnter={() => setInCodeCopy(true)}
        >
          <Tooltip title={
            <h3>
              {intl.formatMessage({
                id: inCodeCopy && copiedCodeToClipboard ? 'uclusionAttachmentLinkCopied' : 
                'uclusionAttachmentLinkDirections',
              })}
            </h3>
          } placement="top">
            <FileCopyIcon htmlColor="#2F80ED" style={{ fontSize: 14 }} />
          </Tooltip>
        </IconButton>
  )
}

export function displayLinksList(filesList, fileBaseUrl, marketId, onDeleteClick, classes) {
  return (filesList || []).map((file) => {
    const {original_name, path} = file;
    const linkToFile = `${fileBaseUrl}/${path}`;

    /** Since the service worker doesn't want to fire for external links,
     * we'll do its job for it and click the new url
     * @param link
     * @param originalName
     * @returns {*}
     */
    function downloadFile(link, originalName) {
      return getMarketLogin(marketId)
        .then((results) => {
          const { uclusion_token } = results;
          const newUrl = `${link}?authorization=${uclusion_token}`;
          const linkEl = document.createElement('a')
          linkEl.href = newUrl;
          linkEl.target = '_';
          linkEl.download = originalName;
          linkEl.click();
        });
    }

    return (
      <>
      {onDeleteClick && (
        <FileCopyLinker linkToFile={linkToFile} />
      )}
      <div style={{ maxWidth: '200px' }}>
        <Link
          href={linkToFile}
          variant="inherit"
          underline="always"
          color="primary"
          download={original_name}
          onClick={(e) => {
            e.preventDefault();
            return downloadFile(linkToFile, original_name);
          }}
          className={classes.file}
        >
          {original_name}
        </Link>
        {onDeleteClick && (
          <SpinningTooltipIconButton
            id='deleteFiles'
            translationId="delete"
            edge="end"
            onClick={() => {
                return onDeleteClick(path)
            }}
            icon={<NotificationDeletion isRed />}
            aria-label="delete"
          />
        )}
      </div>
      </>
    )
  })
}

function AttachedFilesList(props) {

  const { marketId, attachedFiles = [], onUpload = () => {}, onDeleteClick } = props;
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const classes = attachedFilesStyles();
  const intl = useIntl();
  const fileBaseUrl = config.file_download_configuration.baseURL;
  const hasFiles = !_.isEmpty(attachedFiles);

  return (
    <LoadingOverlay
      active={uploadInProgress}
      spinner
      text={intl.formatMessage({ id: 'uploadInProgress' })}
    >
      <div className={classes.sectionTitle}>
        <FormattedMessage id="attachedFilesSection"/>
      </div>
      {!hasFiles && (
        <FileUploader marketId={marketId} onUpload={onUpload} setUploadInProgress={setUploadInProgress} />
      )}
      {hasFiles && (
      <div>
        <FileUploader key="uploader" marketId={marketId} onUpload={onUpload}
                      setUploadInProgress={setUploadInProgress}/>
        {displayLinksList(attachedFiles, fileBaseUrl, marketId, onDeleteClick, classes)}
      </div>
      )}
    </LoadingOverlay>
  );
}

AttachedFilesList.propTypes = {
  onUpload: PropTypes.func,
  attachedFiles: PropTypes.arrayOf(PropTypes.object),
  marketId: PropTypes.string.isRequired,
  onDeleteClick: PropTypes.func
};

export default AttachedFilesList;